import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { SelectionPoint } from './selection-point';

interface DropdownData {
  selectionPoint: SelectionPoint;
  options: DropdownOption[];
  selectedValue?: any;
  showDropdown: boolean;  // Control visibility of the dropdown in the UI
}

interface DropdownOption {
  displayValue: string;
  value: string | null | undefined;
}


// NOTE: this adds a drop down for each selection point as it progresses through the selection process.
//       It is just that the code doesn't show every single drop down

@Component({
  selector: 'pgh-array-object-selector',
  templateUrl: './array-object-selector.component.html',
  styleUrls: ['./array-object-selector.component.scss']
})
export class ArrayObjectSelectorComponent implements OnInit, OnDestroy, OnChanges {

  @Input() public selectionPoints: SelectionPoint[] = [];
  @Input() public objects: any[] = [];

  @Output() public selectionChanged = new EventEmitter<any>();
  @Output() public multipleObjectsDetected = new EventEmitter<any[]>();


  private _selectedObject: any = undefined;

  public dropdowns: DropdownData[] = [];

  public ngOnInit() {
  }

  // This is called before ngOnInit is called and whenever one or more data-bound input properties change
  public ngOnChanges(changes: SimpleChanges): void {
    // The very first time this is called, it seems like both the this.selectionPoints and this.objects are already set

    // Whenever this.objects or this.selectionPoints changes we want to reset this component and use the latest inputs to drive the component.
    this.reset();
  }

  public ngOnDestroy(): void {
    this.selectionChanged.complete();
    this.multipleObjectsDetected.complete();
  }

  private reset() {
    this.dropdowns = [];
    this.setSelectedObject(undefined);

    if ((this.objects?.length ?? 0) < 1) {
      throw new Error('There must be at least 1 object in the objects array for ArrayObjectSelector to work');
    }
    if ((this.selectionPoints?.length ?? 0) < 1) {
      throw new Error('There must be at least 1 item in the selectionPoints array for ArrayObjectSelector to work');
    }

    this.addDropdown(0, this.objects);
  }

  private addDropdown(index: number, filteredObjects: any[]) {
    const selectionPoint = this.selectionPoints[index];

    const values: (string | null | undefined)[] = this.getUniqueValues(selectionPoint, filteredObjects);
    const dropdownOptions: DropdownOption[] = this.buildDropdownOptions(values, selectionPoint.displayValueForNullUndefinedEmptyOrWhitespace);

    const showDropdown = dropdownOptions.length > 1;
    const dropdownData: DropdownData = { selectionPoint, options: dropdownOptions, showDropdown };
    if (dropdownOptions.length === 1) {
      dropdownData.selectedValue = dropdownOptions[0].value;
      dropdownData.showDropdown = false;
      this.dropdowns.push(dropdownData);

      this.updateDropdowns(index, filteredObjects);
    } else {
      this.dropdowns.push(dropdownData);
      this.setSelectedObject(undefined);
    }
  }

  private getUniqueValues(selectionPoint: SelectionPoint, objects: any[]): any[] {
    const uniqueValues = new Set(objects.map(item => this.resolvePropertyPath(item, selectionPoint.propertyPath)));
    return Array.from(uniqueValues).sort();
  }

  private buildDropdownOptions(values: (string | null | undefined)[], displayValueForNullUndefinedEmptyOrWhitespace: string | null | undefined): DropdownOption[] {
    return values.map((v) => {
      let displayValue: string = v;

      const isValueNullUndefinedEmptyOrWhitespace = (v ?? '').trim().length < 1;
      if (displayValueForNullUndefinedEmptyOrWhitespace != null && isValueNullUndefinedEmptyOrWhitespace) {
        displayValue = displayValueForNullUndefinedEmptyOrWhitespace;
      }

      return {
        displayValue: displayValue,
        value: v,
      };
    });
  }

  public onSelectValue(index: number, option: DropdownOption) {
    this.dropdowns[index].selectedValue = option.value;
    this.updateDropdowns(index, this.filterObjects(index));
  }

  private updateDropdowns(currentIndex: number, filteredObjects: any[]) {
    this.dropdowns.splice(currentIndex + 1);
    if (currentIndex + 1 < this.selectionPoints.length) {
      this.addDropdown(currentIndex + 1, filteredObjects);
    } else if (filteredObjects.length === 1) {
      this.setSelectedObject(filteredObjects[0]);
    } else if (filteredObjects.length > 1) {
      // User selected the last drop down that was listed in the selectionPoints array and there is still more than 1 item matching all the selections made
      this.setSelectedObject(undefined);
      this.multipleObjectsDetected.emit(filteredObjects);
    }
  }

  private filterObjects(upToIndex: number): any[] {
    return this.objects.filter(obj =>
      this.dropdowns.slice(0, upToIndex + 1).every(dropdown =>
        this.resolvePropertyPath(obj, dropdown.selectionPoint.propertyPath) === dropdown.selectedValue
      ));
  }

  private resolvePropertyPath(obj: any, path: string): any {
    return path.split('.').reduce((o, key) => (o && o[key] !== undefined) ? o[key] : undefined, obj);
  }

  public getSelectedObject() {
    return this._selectedObject;
  }
  private setSelectedObject(newObject: any) {
    if (this._selectedObject !== newObject) {
      this._selectedObject = newObject;
      this.selectionChanged.emit(this._selectedObject);
    }
  }
}
